import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ClientOut,
  IceServerCfg,
  ServerIn,
  ServerErrorMsg,
} from './protocol';
import { useSignaling } from './useSignaling';
import { usePeerConnection, type PeerStatus, type UsePeerConnectionApi } from './usePeerConnection';

export type SessionPhase =
  | { kind: 'idle' }
  | { kind: 'connecting_signaling' }
  | { kind: 'authenticating' }
  | { kind: 'negotiating' }
  | { kind: 'connected' }
  | { kind: 'failed'; reason: string };

export interface UseSessionOpts {
  signalingUrl: string;
  hostId: string;
  /** Empty string means "no password, use bearer token instead". */
  password: string;
  /** Optional JWT — when supplied, signaling will accept the connection without a password as long
   *  as the host is in the user's trusted_devices. */
  bearerToken?: string | null;
}

export interface UseSessionApi {
  phase: SessionPhase;
  peerStatus: PeerStatus;
  attachVideo: (el: HTMLVideoElement | null) => void;
  /// Ref to the live RTCPeerConnection — used by `StatsBar` to poll getStats() inside its own
  /// component subtree without triggering re-renders of the whole `SessionPage`.
  pcRef: React.RefObject<RTCPeerConnection | null>;
  inputChannel: RTCDataChannel | null;
  controlChannel: RTCDataChannel | null;
  filesChannel: RTCDataChannel | null;
  reconnect: () => void;
  disconnect: () => void;
}

/**
 * Orchestrates the full client-side session: signaling handshake → ICE servers →
 * WebRTC offer → SDP answer → ICE trickling → connected.
 */
export function useSession({ signalingUrl, hostId, password, bearerToken }: UseSessionOpts): UseSessionApi {
  const [phase, setPhase] = useState<SessionPhase>({ kind: 'idle' });
  const [iceServers, setIceServers] = useState<IceServerCfg[] | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const inputDcRef = useRef<RTCDataChannel | null>(null);
  const controlDcRef = useRef<RTCDataChannel | null>(null);
  const filesDcRef = useRef<RTCDataChannel | null>(null);
  const remoteStreamRef = useRef<MediaStream>(new MediaStream());
  const pcApiRef = useRef<UsePeerConnectionApi | null>(null);

  const sendRef = useRef<((m: ClientOut) => boolean) | null>(null);
  const errorRef = useRef<string | null>(null);

  const onLocalIce = useCallback((c: RTCIceCandidate) => {
    sendRef.current?.({
      type: 'ice_candidate',
      candidate: c.candidate,
      sdpMid: c.sdpMid ?? null,
      sdpMLineIndex: c.sdpMLineIndex ?? null,
    });
  }, []);

  const onTrack = useCallback((track: MediaStreamTrack, _streams: readonly MediaStream[]) => {
    // str0m emits each track in its own MediaStream object (no shared msid). If we ever assign
    // `remoteStreamRef.current = streams[0]`, the SECOND ontrack event would overwrite the first —
    // we'd end up with a stream containing only the LAST received track (typically audio, which
    // arrives after video). Instead, we ALWAYS push incoming tracks onto our own combined stream
    // so the <video> element sees both video and audio.
    if (!remoteStreamRef.current.getTracks().some((t) => t.id === track.id)) {
      remoteStreamRef.current.addTrack(track);
    }
    if (videoRef.current && videoRef.current.srcObject !== remoteStreamRef.current) {
      videoRef.current.srcObject = remoteStreamRef.current;
    }
  }, []);

  const onDataChannel = useCallback((ch: RTCDataChannel) => {
    if (ch.label === 'input') inputDcRef.current = ch;
    else if (ch.label === 'control') controlDcRef.current = ch;
    else if (ch.label === 'files') filesDcRef.current = ch;
  }, []);

  const pcApi = usePeerConnection({ iceServers, onLocalIce, onTrack, onDataChannel });
  pcApiRef.current = pcApi;

  const onSignalingMessage = useCallback(
    (msg: ServerIn) => {
      void (async () => {
        switch (msg.type) {
          case 'accepted': {
            setIceServers(msg.iceServers);
            setPhase({ kind: 'negotiating' });
            const pc = pcApi.open();
            // Re-apply the freshly-known iceServers (open() captures the value present at call time).
            pc.setConfiguration({
              iceServers: msg.iceServers,
              bundlePolicy: 'max-bundle',
              rtcpMuxPolicy: 'require',
            });
            const sdp = await pcApi.createOffer();
            sendRef.current?.({ type: 'sdp_offer', sdp });
            break;
          }
          case 'sdp_answer':
            await pcApi.applyRemoteAnswer(msg.sdp);
            break;
          case 'ice_candidate':
            await pcApi.addRemoteIce(msg.candidate, msg.sdpMid ?? null, msg.sdpMLineIndex ?? null);
            break;
          case 'host_disconnected':
            setPhase({ kind: 'failed', reason: 'host disconnected' });
            break;
          case 'renegotiate': {
            // Phase A.2 — host initiated an ICE restart. Apply the new offer, build an answer,
            // send it back. The PC stays alive (same media, same DCs); only ICE creds + candidates
            // rotate. If `acceptRemoteOffer` throws, we log + ignore — the existing session keeps
            // running on the old creds until coturn's TTL kicks in (the host should retry on next
            // periodic tick).
            try {
              const answer = await pcApi.acceptRemoteOffer(msg.sdp);
              sendRef.current?.({ type: 'renegotiate_answer', sdp: answer });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('renegotiate (ICE restart) failed:', err);
            }
            break;
          }
          case 'error':
            errorRef.current = errorMessage(msg);
            setPhase({ kind: 'failed', reason: errorRef.current });
            break;
        }
      })();
    },
    [pcApi],
  );

  const signaling = useSignaling({ url: signalingUrl, onMessage: onSignalingMessage, noReconnect: true });
  sendRef.current = signaling.send;

  // Send `client_hello` as soon as the WS is open.
  const helloSentRef = useRef(false);
  useEffect(() => {
    if (signaling.status.kind === 'open' && !helloSentRef.current) {
      helloSentRef.current = true;
      setPhase({ kind: 'authenticating' });
      // Bearer-token auth path: signed-in user connecting to a trusted device → password optional.
      const hello: ClientOut = bearerToken
        ? { type: 'client_hello', hostId, ...(password ? { password } : {}), bearerToken }
        : { type: 'client_hello', hostId, password };
      signaling.send(hello);
    } else if (signaling.status.kind === 'connecting') {
      setPhase((p) => (p.kind === 'idle' ? { kind: 'connecting_signaling' } : p));
    } else if (signaling.status.kind === 'closed' && !helloSentRef.current) {
      setPhase({ kind: 'failed', reason: `signaling closed (code ${signaling.status.code})` });
    }
    // `helloSentRef` ensures we only fire once per WS lifecycle, so re-renders that change
    // `bearerToken` (rare — would happen if the user re-logged-in mid-session) don't trigger a
    // duplicate hello. Including bearerToken in the deps closes a subtle bug where the FIRST
    // hello fired before the auth store finished hydrating and went out without the token.
  }, [signaling.status, signaling, hostId, password, bearerToken]);

  // Promote to `connected` once the peer is fully up.
  useEffect(() => {
    if (pcApi.status.kind === 'connected') setPhase({ kind: 'connected' });
    if (pcApi.status.kind === 'failed') setPhase({ kind: 'failed', reason: pcApi.status.reason });
  }, [pcApi.status]);

  // Stats polling lives in `StatsBar` (own component, own state) to keep the high-frequency
  // setState out of `SessionPage`'s render path. Re-rendering the whole page once per second
  // would force Android Chromium's video-overlay compositor to flip on every tick — visible as
  // a black flash. Containing the state to `StatsBar` lets the rest of the tree stay stable.

  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (el) {
      el.srcObject = remoteStreamRef.current;
      el.muted = true; // mobile autoplay requires muted on first attach
      el.playsInline = true;
      el.autoplay = true;
    }
  }, []);

  const reconnect = useCallback(() => {
    helloSentRef.current = false;
    setPhase({ kind: 'idle' });
    // useSignaling will re-create the WS on next mount; for now just reload the page.
    window.location.reload();
  }, []);

  const disconnect = useCallback(() => {
    pcApi.close();
    signaling.close(1000, 'user disconnect');
    setPhase({ kind: 'idle' });
  }, [pcApi, signaling]);

  return {
    phase,
    peerStatus: pcApi.status,
    attachVideo,
    pcRef: pcApi.pcRef,
    inputChannel: inputDcRef.current,
    controlChannel: controlDcRef.current,
    filesChannel: filesDcRef.current,
    reconnect,
    disconnect,
  };
}

function errorMessage(msg: ServerErrorMsg): string {
  switch (msg.code) {
    case 'host_offline':
      return 'Host is offline or unknown';
    case 'bad_password':
      return 'Wrong password';
    case 'rate_limited':
      return msg.retryAfterSeconds
        ? `Too many attempts — try again in ${msg.retryAfterSeconds}s`
        : 'Too many attempts — try later';
    case 'unauthorized':
      return 'Unauthorized';
    case 'host_disconnected':
      return 'Host disconnected mid-session';
    case 'invalid_message':
      return `Protocol error: ${msg.message}`;
    case 'internal_error':
      return 'Server error — try again';
    default:
      return msg.message ?? 'Unknown error';
  }
}
